import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1572459815549-873917ec8c0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxmaXQlMjBibGFjayUyMGNvdXBsZSUyMGd5bXxlbnwwfHx8fDE3NzcxNTg5OTF8MA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end pt-24 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Fit Black couple training together in the gym"
          className="w-full h-full object-cover object-right md:object-center"
          loading="eager"
        />
        {/* Gradient + dark overlay for legibility (focus left side) */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/40" />
      </div>

      {/* Floating volt accent */}
      <div className="absolute right-[-10%] top-[20%] w-[420px] h-[420px] rounded-full bg-[#C6FF00]/10 blur-[120px] pointer-events-none" />

      <div className="relative z-10 container mx-auto max-w-7xl px-6 md:px-10 w-full">
        <div className="max-w-3xl">
          <div className="swole-rise inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 backdrop-blur-md border border-white/10 mb-8">
            <span className="w-2 h-2 rounded-full bg-[#C6FF00] animate-pulse" />
            <span className="text-xs uppercase tracking-[0.25em] text-neutral-200 font-semibold">
              Now in Early Access
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-heading text-[clamp(3.25rem,11vw,9rem)] leading-[0.9] tracking-tight text-white swole-rise"
            style={{ animationDelay: "0.1s" }}
          >
            FIND YOUR
            <br />
            <span className="text-[#C6FF00] [text-shadow:0_0_40px_rgba(198,255,0,0.35)]">
              SWOLEMATE
            </span>
          </h1>

          <p
            className="mt-8 text-base md:text-lg text-neutral-300 max-w-xl leading-relaxed swole-rise"
            style={{ animationDelay: "0.25s" }}
          >
            The fitness dating app for people who want a real partner — in the
            gym and in life. Build chemistry through sweat, sets, and shared
            goals.
          </p>

          <p
            className="mt-3 text-sm uppercase tracking-[0.25em] text-[#C6FF00]/90 font-semibold swole-rise"
            style={{ animationDelay: "0.35s" }}
          >
            Match. Set a workout. Show up.
          </p>

          <div
            className="mt-10 flex flex-col sm:flex-row gap-4 swole-rise"
            style={{ animationDelay: "0.45s" }}
          >
            <button
              data-testid="hero-join-now-btn"
              onClick={() => (window.location.href = "/register")}
              className="group inline-flex items-center justify-center gap-2 bg-[#C6FF00] hover:bg-[#AEE600] text-[#0A0A0A] font-bold text-base uppercase tracking-[0.2em] px-8 py-4 rounded-md transition-all duration-300 hover:scale-105 shadow-[0_0_24px_rgba(198,255,0,0.35)] hover:shadow-[0_0_44px_rgba(198,255,0,0.55)]"
            >
              Join Now
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>

            <button
              data-testid="hero-how-it-works-btn"
              onClick={() => scrollTo("#how")}
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/30 hover:border-[#C6FF00] hover:text-[#C6FF00] text-white font-bold text-base uppercase tracking-[0.2em] px-8 py-4 rounded-md transition-all duration-300"
            >
              <Play size={16} />
              See How It Works
            </button>
          </div>

          {/* Stat strip */}
          <div
            className="mt-16 grid grid-cols-3 gap-6 max-w-xl swole-rise"
            style={{ animationDelay: "0.6s" }}
          >
            {[
              { v: "12K+", l: "Active members" },
              { v: "72h", l: "Match window" },
              { v: "4.9★", l: "Early reviews" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-[#C6FF00]/60 pl-3">
                <div className="font-heading text-3xl md:text-4xl text-white">
                  {s.v}
                </div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-neutral-400 mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
